import _ from 'lodash';
import { AppDispatch } from '../../../store.type';
import { getContentSchemaFromContentFul } from '../../../redux/actions/common/common-action';
import { CmsItem, DEVICE_METADATA_FIELDS, FaqNode, HelpCenterData, METADATA_FIELDS } from './index';
import { HELP_CENTER } from '../../../utils/constants';
import { AMAZE, TICKET_PROPERTIES, TRANSACTION_REQUIRED_FORM_FIELD } from './constants';
import {
  openTimelineModal,
  processTimelineData
} from '../../../utils/utils-transaction-history/utils-transaction-history';
import {
  setConfigurationdata,
  setCurrentLevelItems,
  setHelpCenterLabels
} from '../../../redux/actions/help-center-action/help-center-actions';

export function transformData(
  data: CmsItem[],
  breadCrumbs: string[] = [],
  level = 1,
  parent: CmsItem = null,
  userStatus = 'ALL',
  countryCode: string
): FaqNode[] {
  const transformedData: FaqNode[] = [];
  if (Array.isArray(data)) {
    data?.forEach((item, index) => {
      const faqContent = item?.target?.fields;
      const id = faqContent?.id?.value || '';
      const includeCountries = faqContent?.includeCountries?.value || [];
      const excludeCountries = faqContent?.excludeCountries?.value || [];
      const status = faqContent?.showIfUserStatus?.value || [];
      const zendeskGroupId = faqContent?.groupId?.target?.fields?.zendeskGroupId?.value || '';
      const customerSupportChannelHierarchy =
        faqContent?.customerSupportChannelHierarchy?.value || null;
      const priority = faqContent?.priority?.value || 'low';
      // Check country included

      if (
        isUserStatusIncluded(status, userStatus) &&
        checkCountry(countryCode, includeCountries, excludeCountries)
      ) {
        const currentBreadcrumb = [...breadCrumbs, faqContent?.header?.value]; // Update breadCrumbs
        const entry: FaqNode = {
          level,
          index,
          header: faqContent?.header?.value,
          iconName: faqContent?.iconName?.value,
          breadCrumbs: currentBreadcrumb,
          parentRef: parent,
          weight: faqContent?.weight?.value || 1,
          keywords: faqContent?.keywords,
          status,
          id,
          zendeskGroupId,
          customerSupportChannelHierarchy,
          priority
        };
        if (faqContent?.helpCenterItem) {
          /* If node contains help center item,
             recursively transform the data */
          entry.data = transformData(
            faqContent?.helpCenterItem?.value,
            currentBreadcrumb,
            level + 1,
            entry,
            userStatus,
            countryCode
          );
        } else {
          /* If node doesn't contains help center item,
             treat it as a last item which is final FAQ content */
          const inputContent = faqContent?.inputFields?.target?.fields;
          entry.faqContent = prepareFaqContent(faqContent, inputContent);
        }
        transformedData.push(entry);
      }
    });
  }
  return transformedData;
}

export const getContentSchema = async (
  userStatus: string,
  countryCode: string
): Promise<{ transformedData: FaqNode[]; labels: any; configurationData: any }> => {
  const response = await getContentSchemaFromContentFul('helpCenter', 'HELP_CENTER');
  const jsonData = response?.helpCenterItem?.value || {};
  const labels = response?.labels?.value || {};
  const helpCenterTagsList = response?.helpCenterTagsList?.value || {};
  const configurationData = response?.configurations?.value || {};
  const customerFeedbackLabels = response?.customerFeedbackLabels?.value || {};
  // Transform the data
  const transformedData = transformData(
    jsonData,
    [labels?.helpCenterHome],
    1,
    null,
    userStatus,
    countryCode
  );
  return {
    transformedData,
    configurationData,
    labels: { labelTexts: labels, helpCenterTagsList, customerFeedbackLabels }
  };
};

function prepareFaqContent(faqContent: CmsItem, inputContent: CmsItem) {
  return {
    title: inputContent?.title?.value,
    header: faqContent?.header?.value,
    groupId: faqContent?.groupId?.target?.fields?.value?.value,
    zendeskGroupId: faqContent?.groupId?.target?.fields?.zendeskGroupId?.value,
    richTextContent: faqContent?.descriptions?.value,
    dropdownValues: inputContent?.dropdownValues?.value,
    dropdownLabel: inputContent?.formLabels?.value?.dropdownLabel,
    optionalFields: inputContent?.optionalFields?.value,
    description: inputContent?.description?.value,
    textAreaPlaceholder: inputContent?.formLabels?.value?.textAreaPlaceholder,
    textInputPlaceholder: inputContent?.formLabels?.value?.textInputPlaceholder,
    textAreaLabel: inputContent?.formLabels?.value?.textAreaLabel,
    textInputLabel: inputContent?.formLabels?.value?.textInputLabel,
    attachmentHeading: inputContent?.formLabels?.value?.attachmentHeading,
    emptyTextInputLabel: inputContent?.formLabels?.value?.emptyTextInputLabel,
    discouragePopup: inputContent?.discouragePopup?.value,
    type: inputContent?.type?.value,
    keywords: faqContent?.keywords?.value,
    weight: faqContent?.weight?.value || 1,
    subject: faqContent?.key?.value
  };
}

function isUserStatusIncluded(statusArray: string[], status: string): boolean {
  if (statusArray.length === 0) return true;
  return statusArray.includes(status);
}

function checkCountry(countryCode: string, includeCountries: string[], excludeCountries: string[]) {
  // true if both lists are empty
  if (includeCountries.length === 0 && excludeCountries.length === 0) return true;
  // check if excluded country list contains country code
  if (includeCountries.length === 0) return !excludeCountries.includes(countryCode);
  // check if included country list contains country code
  if (excludeCountries.length === 0) return includeCountries.includes(countryCode);
  // finally check both lists
  return includeCountries.includes(countryCode) && !excludeCountries.includes(countryCode);
}

/* To get the priority of customer support channel (Chat or Ticket creation).
   Based on the country and the level-1 help center item
   configured on help center configuration. */
export const getCustomerSupportChannelHierarchy = (
  helpCenterItem: FaqNode,
  config: Record<string, any>,
  countryCode: string
): string[] => {
  const level1Node = getL1HelpCenterItem(helpCenterItem); // Get the level-1 node.
  const countryConfig =
    config?.customerSupportChannelHierarchy?.[countryCode] ||
    config?.customerSupportChannelHierarchy?.default; // Check country configuration else use default.

  // Check ID configuration else use default.
  if (level1Node?.id && level1Node?.id in countryConfig) {
    return countryConfig?.[level1Node?.id];
  }
  return countryConfig?.default;
};

//   // To get the level-1(First level of help center item) from any help center item.
export const getL1HelpCenterItem = (helpCenterItem: FaqNode): FaqNode => {
  while (helpCenterItem?.parentRef) {
    helpCenterItem = helpCenterItem.parentRef;
  }
  return helpCenterItem;
};

const encodeStringToZendeskKeyFormat = (key: string) =>
  key?.replace(/[^\w-.^~/]/g, '_')?.toLowerCase();

export const encodeToZendeskKeyFormat = (
  data: string[] | string,
  excludeFirstEntry: boolean = false
) => {
  if (Array.isArray(data)) {
    let listData = data;
    if (excludeFirstEntry) {
      listData = data?.slice(1);
    }

    return listData?.flat()?.map(encodeStringToZendeskKeyFormat)?.join('__');
  } else {
    return encodeStringToZendeskKeyFormat(data || '');
  }
};

export const getValueFromArray = (array: any, key: string) => {
  return _.find(array, { key: key })?.value;
};

export const getSupportHierarchy = (faqItem: FaqNode, countryCode: string): Array<string> => {
  if (countryCode === 'IN') return ['ticket'];
  if (faqItem) {
    if (faqItem.customerSupportChannelHierarchy)
      return faqItem.customerSupportChannelHierarchy?.map((type) => type.toLowerCase());
    return getSupportHierarchy(faqItem.parentRef, countryCode);
  }
  return ['ticket', 'chat'];
};
export const handleRowSelected = (rowClicked: any, context: any) => {
  const {
    isNiumForexUser,
    currentLevelItems,
    remitterId,
    raiseNewTicket,
    enquiryInitiatedBackNav,
    setHelpCenterSelectedTransaction,
    setTransactionHistoryState,
    setRaiseEnquiry,
    transactionHistoryState,
    toggleIsNewTransactionChatCreated,
    country_code: countryCode
  } = context?.props || '';
  const { timelineData, timelineCMSLabels, transactionSummary, lastSelectedTransactionId } =
    context.state;
  const isHelpCenterFlag = context?.props?.location?.pathname === HELP_CENTER;
  const timeline = !isNiumForexUser && processTimelineData(timelineData?.timeline || []);
  const { transaction_id, transaction_number } = rowClicked;
  if (isHelpCenterFlag) {
    if (transactionHistoryState?.isChatInitialisation) {
      const conversationFields = [
        ...DEVICE_METADATA_FIELDS,
        {
          id: METADATA_FIELDS.levelOneSubject,
          value: encodeToZendeskKeyFormat(currentLevelItems?.breadCrumbs, true)
        },
        {
          id: METADATA_FIELDS.countryCode,
          value: countryCode?.toLowerCase()
        },
        {
          id: METADATA_FIELDS.uniqueUserId,
          value: remitterId
        },
        {
          id: METADATA_FIELDS.chatSubject,
          value: currentLevelItems?.header || ''
        },
        {
          id: METADATA_FIELDS.transactionNumber,
          value: transaction_number || ''
        }
      ];
      // @ts-ignore
      const { zE: zenDesk } = window;
      if (zenDesk) {
        zenDesk('messenger:set', 'conversationFields', conversationFields);
        zenDesk('messenger', 'open');

        const isTransactionRequired =
          currentLevelItems?.faqContent?.type?.includes(TRANSACTION_REQUIRED_FORM_FIELD) &&
          !currentLevelItems?.parentRef?.breadCrumbs?.includes(AMAZE) &&
          transactionHistoryState.historyTableData.length > 0;
        setHelpCenterSelectedTransaction({});
        raiseNewTicket(false);
        setRaiseEnquiry(false);
        enquiryInitiatedBackNav(false);
        setTransactionHistoryState((prevState: any) => ({
          ...prevState,
          isEnquiryRequiredTransactionIdFlag: isTransactionRequired,
          activeComponent: ''
        }));
        toggleIsNewTransactionChatCreated(true);
      }
    } else {
      const { currentHelpCentreTickets = [] } = context?.props || {};
      const isTransactionNoPresentAndOpen = (transactionNo: string) => {
        return currentHelpCentreTickets?.some((ticket: any) => {
          if (
            ticket?.status.toLowerCase() === TICKET_PROPERTIES.status.open &&
            ticket?.customFields
          ) {
            return ticket?.customFields.some(
              (field: any) => field?.key === 'transactionNo' && field?.value === transactionNo
            );
          } else {
            return false;
          }
        });
      };
      const isTransactionNoPresentAndOpenFlag = isTransactionNoPresentAndOpen(transaction_number);
      openTimelineModal(
        context,
        lastSelectedTransactionId,
        transaction_id,
        transaction_number,
        timelineCMSLabels,
        transactionSummary,
        timelineData,
        timeline,
        isTransactionNoPresentAndOpenFlag
      );
    }
  }
};

export const handleHCHyperLinkOrTextSearch = (
  data: HelpCenterData,
  keyOrSearchString: string | string[] | undefined, // Can be either a search string or an array of keys
  dispatch: AppDispatch
): any => {
  // Check if it's a key array (hyperlink-based search)
  if (Array.isArray(keyOrSearchString)) {
    const hcData: any = keyOrSearchString.reduce(
      (data, id) => data?.data?.find((item: any) => item?.id === id),
      data
    );
    if (hcData) {
      dispatch(setCurrentLevelItems(hcData));
      return hcData;
    }
  }
  // Otherwise, perform a text search for entry-hyperlink
  else if (typeof keyOrSearchString === 'string' && Array.isArray(data)) {
    for (const item of data) {
      const headerMatch = item?.header?.toLowerCase() === keyOrSearchString.toLowerCase();
      const faqContentMatch =
        item?.faqContent?.header?.toLowerCase() === keyOrSearchString.toLowerCase();

      if (headerMatch || faqContentMatch) {
        dispatch(setCurrentLevelItems(item));
        return;
      }
      handleHCHyperLinkOrTextSearch(item.data, keyOrSearchString, dispatch);
    }
  }
  return null;
};

export const getData = async (
  dispatch: any,
  countryCode: string,
  status: string,
  persistCurrentLevelItems = false
) => {
  const {
    transformedData: data,
    labels,
    configurationData
  } = await getContentSchema(status, countryCode);
  !persistCurrentLevelItems && dispatch(setCurrentLevelItems({ data, level: 0 }));
  dispatch(setConfigurationdata(configurationData));
  if (labels) {
    dispatch(setHelpCenterLabels(labels));
  }
  return { data, level: 0 };
};

export const getHelpCenterDeeplinkCMSData = async () => {
  const cmsData = await getContentSchemaFromContentFul('component', 'HELP_CENTER_DEEPLINK');

  return cmsData.labels?.value || {};
};
